import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldAlert, Clock, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * ZATCA Phase-2 reporting indicator.
 * Green dot = onboarded AND the last 10 invoices are all reported/cleared.
 * Red dot   = not onboarded, OR any of the last 10 invoices FAILED/REJECTED.
 * Re-polls /zatca-p2/status every 60s so the colour tracks live invoice health.
 * When `interactive` is set, the dot becomes a button that opens a popup with a
 * "Retry Submission" action that re-triggers ZATCA reporting for the most recent
 * failed order (reuses POST /zatca-p2/report-order/{id}).
 */
export function ZatcaStatusDot({ showLabel = true, className = '', interactive = false }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/zatca-p2/status');
      setState(r.data);
    } catch {
      setState({ reporting: false, _error: true });
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 60000); // auto-refresh every 60s while visible
    return () => clearInterval(id);
  }, [load]);

  if (!state) return null;

  const reporting = !!state.reporting;
  const recent = state.recent || null;
  const hasFailures = !!(recent && recent.failed > 0);
  // Green only when onboarded AND no failures in the last 10 invoices.
  const healthy = reporting && !hasFailures;
  const label = healthy ? 'Reporting' : (hasFailures ? 'Failed invoices' : 'Not Reporting');

  const dot = (
    <span
      className={`inline-block w-2 h-2 rounded-full ${healthy ? 'bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.2)]' : 'bg-rose-500 shadow-[0_0_0_2px_rgba(244,63,94,0.2)]'}`}
    />
  );
  const text = showLabel && (
    <span className={`text-[10px] font-semibold ${healthy ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
      ZATCA
    </span>
  );

  if (!interactive) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${className}`}
        title={`ZATCA e-invoicing: ${label}`}
        data-testid="zatca-status-dot"
        data-reporting={healthy}
      >
        {dot}{text}
      </span>
    );
  }

  const lastReported = state.last_reported_at
    ? new Date(state.last_reported_at).toLocaleString()
    : null;

  const retryFailed = async () => {
    if (!state.retry_order_id) return;
    setRetrying(true);
    const tId = toast.loading('Re-submitting invoice to ZATCA…');
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const { data } = await api.post(
        `/zatca-p2/report-order/${state.retry_order_id}`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      toast.success(data.message || 'Re-submitted to ZATCA ✓', { id: tId });
      await load(); // refresh health + dot colour
    } catch (e) {
      toast.error(e.response?.data?.detail || 'ZATCA re-submission failed', { id: tId });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 min-h-10 rounded-full border px-2 py-1 transition-colors hover:bg-accent ${healthy ? 'border-emerald-200' : 'border-rose-200'} ${className}`}
        title="View ZATCA e-invoicing status"
        data-testid="zatca-status-btn"
        data-reporting={healthy}
      >
        {dot}{text}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="zatca-status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {healthy
                ? <ShieldCheck size={18} className="text-emerald-600" />
                : <ShieldAlert size={18} className="text-rose-600" />}
              ZATCA e-Invoicing
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm text-muted-foreground">Status</span>
              <span
                className={`inline-flex items-center gap-1.5 text-sm font-bold ${healthy ? 'text-emerald-600' : 'text-rose-600'}`}
                data-testid="zatca-status-value"
              >
                {dot}{reporting ? 'Active' : 'Inactive'}
              </span>
            </div>
            {state.environment && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="text-sm text-muted-foreground">Environment</span>
                <span className="text-sm font-semibold capitalize">{state.environment}</span>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5"><Clock size={14} /> Last reported</span>
              <span className="text-sm font-semibold" data-testid="zatca-last-reported">
                {lastReported || (reporting ? 'No invoices reported yet' : '—')}
              </span>
            </div>

            {recent && recent.total > 0 && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="text-sm text-muted-foreground">Last {recent.total} invoices</span>
                <span className="text-sm font-semibold" data-testid="zatca-recent-summary">
                  <span className="text-emerald-600">{recent.reported} ok</span>
                  {recent.failed > 0 && <span className="text-rose-600"> · {recent.failed} failed</span>}
                  {recent.pending > 0 && <span className="text-amber-600"> · {recent.pending} pending</span>}
                </span>
              </div>
            )}

            {hasFailures && state.retry_order_id && (
              <div className="rounded-lg border-2 border-rose-200 bg-rose-50 dark:bg-rose-950/20 p-3 space-y-2" data-testid="zatca-retry-block">
                <p className="text-xs text-rose-700 dark:text-rose-300 flex items-start gap-1.5">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  Invoice <span className="font-mono font-semibold">{state.retry_invoice_number}</span> failed to report. Re-submit it to ZATCA without re-entering the sale.
                </p>
                <Button
                  onClick={retryFailed}
                  disabled={retrying}
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  data-testid="zatca-retry-btn"
                >
                  {retrying ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <RefreshCw size={15} className="mr-1.5" />}
                  {retrying ? 'Re-submitting…' : 'Retry Submission'}
                </Button>
              </div>
            )}

            {!reporting && (
              <p className="text-xs text-muted-foreground">
                Phase 2 is not onboarded on this terminal. Invoices are not being cleared/reported to ZATCA.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ZatcaStatusDot;
